import { QueryClient } from "@tanstack/react-query";
import { JSONContent } from "@tiptap/core";
import { Editor } from "@tiptap/react";
import axios from "axios";
import { nanoid } from "nanoid";
import { Edge, Node } from "reactflow";
import * as Y from "yjs";

import { Project, SemanticPaper } from "@/types";

import { bulletList, listItem, semanticPaperToTipTapJSON, setNodePageContent } from "./utils";

export const searchPapers = async (query: string) => {
  const baseURL = "https://api.semanticscholar.org/graph/v1/paper/search";
  const key = localStorage.getItem("ldg-semantic-key");
  const queryParams = {
    query,
    fields:
      "title,authors,url,year,abstract,referenceCount,citationCount,isOpenAccess,openAccessPdf",
  };
  const headers = key ? { "x-api-key": key } : {};
  return axios.get(baseURL, { params: queryParams, headers });
};

export const findReferences = async (id: string) => {
  const baseURL = `https://api.semanticscholar.org/graph/v1/paper/${id}/references`;
  const key = localStorage.getItem("ldg-semantic-key");
  const queryParams = {
    fields:
      "title,authors,url,year,abstract,referenceCount,citationCount,isOpenAccess,openAccessPdf",
  };
  const headers = key ? { "x-api-key": key } : {};
  return axios.get(baseURL, { params: queryParams, headers });
};

export const findCitations = async (id: string) => {
  const baseURL = `https://api.semanticscholar.org/graph/v1/paper/${id}/citations`;
  const key = localStorage.getItem("ldg-semantic-key");
  const queryParams = {
    fields:
      "title,authors,url,year,abstract,referenceCount,citationCount,isOpenAccess,openAccessPdf",
  };
  const headers = key ? { "x-api-key": key } : {};
  return axios.get(baseURL, { params: queryParams, headers });
};

export const addPapersToGraph = (
  targetNode: Node,
  papers: SemanticPaper[],
  currentProject: Project | undefined,
  nodesMap: Y.Map<Node> | undefined,
  edgesMap: Y.Map<Edge> | undefined,
  editor: Editor,
  queryClient: QueryClient,
  prompt: string
) => {
  console.log(papers);
  if (!papers.length) return;

  const newContent: JSONContent[] = [
    { type: "paragraph" },
    {
      type: "paragraph",
      content: [{ type: "text", marks: [{ type: "bold" }], text: "Semantic query results:" }],
    },
  ];

  const keywordsId = nanoid();
  newContent.push(bulletList([listItem(keywordsId, prompt)]));

  const keywordsNode: Node = {
    id: keywordsId,
    data: { label: prompt },
    position: {
      x: targetNode?.position?.x ?? 0,
      y: (targetNode?.position?.y ?? 0) + 120,
    },
    type: "GraphNode",
    expandParent: false,
    style: {
      width: 200,
      height: 80,
    },
  };
  nodesMap?.set(keywordsId, keywordsNode);
  const id = `edge-${targetNode?.id}-${keywordsId}`;
  edgesMap?.set(id, {
    id,
    source: targetNode?.id,
    target: keywordsId,
    targetHandle: "target-top",
    sourceHandle: "target-bottom",
  } as Edge);

  papers.forEach((paper, i) => {
    console.log(paper);
    const blockId = nanoid();
    newContent.push(bulletList([listItem(blockId, paper?.title)]));

    const roomName = `lateral-ldg-${currentProject?.slug}-${blockId}`;
    const paperJson = semanticPaperToTipTapJSON(paper);
    setNodePageContent(paperJson, roomName, editor.schema);

    const node: Node = {
      id: blockId,
      data: { label: paper?.title, paper },
      position: {
        x: keywordsNode?.position?.x + 210 * i,
        y: keywordsNode?.position?.y + 120,
      },
      type: "GraphNode",
      expandParent: false,
      style: {
        width: 200,
        height: 80,
      },
    };
    nodesMap?.set(blockId, node);
    const id = `edge-${keywordsId}-${blockId}`;
    edgesMap?.set(id, {
      id,
      source: keywordsId,
      target: blockId,
      targetHandle: "target-top",
      sourceHandle: "target-bottom",
    } as Edge);
  });

  // newContent.push(bulletList(list));
  // console.log(list, bulletList(list), newContent);
  editor.commands.insertContentAt(editor.view.state.doc.content.size, newContent);

  queryClient.invalidateQueries({ queryKey: ["pages", currentProject?.id] });
  queryClient.invalidateQueries({ queryKey: ["page-ids"] });
  queryClient.invalidateQueries({ queryKey: ["project-tokens"] });
};
